import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Bell, Calendar, Clock, Repeat, ArrowRight } from "lucide-react";

const features = [
  {
    icon: Bell,
    title: "Smart Notifications",
    description: "Get timely alerts that keep you on track without being intrusive",
  },
  {
    icon: Calendar,
    title: "Calendar Sync",
    description: "Integrate with your calendar for seamless scheduling",
  },
  {
    icon: Clock,
    title: "Custom Timing",
    description: "Set reminders for exactly when you need them",
  },
  {
    icon: Repeat,
    title: "Recurring Tasks",
    description: "Automate regular reminders for daily or weekly tasks",
  },
];

const Reminders = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Reminders - Smart Study Notifications"
        description="Smart study reminders with calendar sync. Set custom notifications, recurring tasks, and never miss a study session or deadline again."
        keywords="study reminders, calendar reminders, task reminders, study notifications, reminder app, study scheduler"
        canonicalUrl="/reminders/"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" />
          
          {/* Animated Glow Effects */}
          <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="text-left space-y-8 animate-fade-in">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                  <div className="w-2 h-2 bg-primary rounded-full animate-glow-pulse" />
                  <span className="text-sm font-medium text-muted-foreground">Never Miss a Deadline</span>
                </div>
                
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                  <span className="text-gradient">Reminders</span>
                </h1>
                
                <p className="text-xl sm:text-2xl text-muted-foreground font-light max-w-xl">
                  Stay on top of your tasks with smart reminders that help you meet every deadline.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button 
                    size="lg" 
                    className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Set Your First Reminder
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Try for Free
                    </a>
                  </Button>
                </div>
                
                <div className="flex items-center gap-8 pt-8">
                  <div>
                    <p className="text-3xl font-bold text-gradient">Smart</p>
                    <p className="text-sm text-muted-foreground">Notifications</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">Recurring</p>
                    <p className="text-sm text-muted-foreground">Reminders</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">Free</p>
                    <p className="text-sm text-muted-foreground">Forever</p>
                  </div>
                </div>
              </div>
              
              {/* Right Video */}
              <div className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
                  <video 
                    src="/videos/productivity-demo.mp4" 
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
                
                {/* Floating Card */}
                <div className="absolute bottom-8 left-8 right-8 bg-card/90 backdrop-blur-md border border-border rounded-2xl p-6 shadow-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Upcoming</p>
                      <p className="text-xl font-bold text-foreground">3 reminders today</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <Bell className="w-6 h-6 text-primary-foreground" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Never Forget{" "}
                <span className="text-gradient">Anything</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Smart reminder features to keep you organized
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={feature.title}
                    className="group bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-xl text-center"
                  >
                    <CardContent className="p-8 space-y-4">
                      <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      
                      <h3 className="text-xl font-semibold text-foreground font-display">
                        {feature.title}
                      </h3>
                      
                      <p className="text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/20 rounded-full blur-[150px]" />
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-display">
              Stay on{" "}
              <span className="text-gradient">Schedule</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              Never miss an important deadline with smart reminders.
            </p>
            <Button 
              size="lg" 
              className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Try Reminders
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Reminders;